import React from 'react';
import { ArrowUpRightIcon, MailIcon, PhoneIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { clinic, finance } from '../data/clinic';
const COMPLAINT_FORM = 'https://excellentcareclinics.nl/klachtenformulier/';

/** Text from the current Klachteninformatie page. */
const steps = [{
  title: 'Klachtenfunctionaris',
  text: 'Eerst behandelt de klachtenfunctionaris uw klacht en zoekt samen met u naar een passende oplossing.'
}, {
  title: 'Klachtencommissie',
  text: 'Bent u daarna niet tevreden over de uitkomst? Dan kunt u bezwaar maken bij de klachtencommissie.'
}, {
  title: 'Stichting Zorggeschil',
  text: 'Komt u er ook daarna niet uit, dan kunt u uw klacht eventueel voorleggen aan een onafhankelijke erkende geschilleninstantie. Excellent Care Clinics is aangesloten bij Stichting Zorggeschil.'
}];
const sections = [{
  title: 'Bespreek uw onvrede eerst met ons',
  body: ['Bent u ergens niet tevreden over? Dan vragen wij u om dit, als dat mogelijk is, eerst te bespreken met de betrokken medewerker of zorgverlener. Vaak kan een misverstand of probleem in een gesprek worden opgelost.', 'Wilt u dit liever niet, komt u er samen niet uit of wilt u direct een klacht indienen? Dan kunt u contact opnemen met onze klachtenfunctionaris.']
}, {
  title: 'Een klacht indienen',
  body: ['Wilt u een klacht, signaal van onvrede of melding indienen? Dan kunt u gebruikmaken van ons klachtenformulier. U kunt hierin uw klacht of melding beschrijven en aangeven hoe u wilt dat wij contact met u opnemen: per e-mail, telefonisch, in een persoonlijk gesprek of alleen ter kennisgeving. Als u dat wilt, kunt u een melding ook anoniem doen.', 'Onze klachtenfunctionaris neemt vervolgens contact met u op en bespreekt samen met u wat nodig is om de klacht goed te behandelen.']
}, {
  title: 'Wat doet de klachtenfunctionaris?',
  body: ['De klachtenfunctionaris luistert naar uw klacht of signaal, geeft uitleg over de klachtenprocedure, kan u helpen bij het formuleren van uw klacht en onderzoekt samen met u en de betrokken medewerker of zorgverlener welke oplossing mogelijk is. De klachtenfunctionaris stelt zich onafhankelijk en onpartijdig op.']
}];
const facts = [{
  title: 'Reactietermijn',
  text: 'Uiterlijk binnen zes weken na ontvangst een schriftelijke reactie. Eén keer verlengen met maximaal vier weken is mogelijk; daarna alleen met uw schriftelijke instemming.'
}, {
  title: 'Kosten',
  text: 'Het indienen en behandelen van een klacht is voor u kosteloos.'
}, {
  title: 'Privacy',
  text: 'Alleen personen die betrokken zijn bij de behandeling van uw klacht hebben toegang tot de informatie die daarvoor nodig is. Wij verwerken persoonsgegevens volgens de AVG.'
}];
export function ComplaintProcedure() {
  return <>
      <PageHeader title="Klachten en signalen van onvrede" crumbs={[{
      label: 'Patiënten'
    }, {
      label: 'Klachteninformatie'
    }]} intro="Bij Excellent Care Clinics doen wij ons uiterste best om u goede, veilige en persoonlijke zorg te bieden. Toch kan het gebeuren dat u niet tevreden bent over onze zorg, communicatie of organisatie. Wij vinden het belangrijk dat u dit aan ons laat weten — alleen dan kunnen wij samen met u naar een oplossing zoeken en onze zorg verbeteren." data-id="element-416" data-mp="6b0r8j6l5khw">
        <div className="mt-8 flex flex-col gap-3 sm:flex-row" data-id="element-417" data-mp="wlayex4h78ox">
          <a href={COMPLAINT_FORM} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary px-6 py-3.5 text-[15px] font-semibold text-white transition-colors duration-150 hover:bg-primary-dark" data-id="element-418" data-mp="11kvzpe74bt4t">
            Naar het klachtenformulier
            <ArrowUpRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-419" data-mp="1g7ywtjrj3zrm" />
          </a>
          <a href={`mailto:${finance.complaintsEmail}`} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-white px-6 py-3.5 text-[15px] font-semibold text-ink ring-1 ring-line transition-colors duration-150 hover:text-primary" data-id="element-420" data-mp="1kbwurr54b4j3">
            <MailIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-421" data-mp="2qs1z3lm0trkz" />
            Mail de klachtenfunctionaris
          </a>
        </div>
      </PageHeader>

      <section className="mx-auto grid max-w-7xl gap-12 px-5 py-16 lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-20 lg:px-8 lg:py-20" data-id="element-422" data-mp="1z2p4ui93aa2n">
        <div className="max-w-3xl space-y-12" data-id="element-423" data-mp="1u4f9a6jgd7gi">
          {sections.map(s => <div key={s.title} data-id="element-424" data-mp="28355ljyg733t">
              <h2 className="font-display text-2xl font-semibold text-ink" data-id="element-425" data-mp="27zzgi4ptk183">{s.title}</h2>
              {s.body.map(p => <p key={p} className="mt-4 text-[17px] leading-relaxed text-muted" data-id="element-426" data-mp="3jh91ivolj1bi">
                  {p}
                </p>)}
            </div>)}

          <div data-id="element-427" data-mp="3eksantq0i8uq">
            <h2 className="font-display text-2xl font-semibold text-ink" data-id="element-428" data-mp="3mzga43njefwh">Niet tevreden over de uitkomst?</h2>
            <p className="mt-4 text-[17px] leading-relaxed text-muted" data-id="element-429" data-mp="3r0rx01f10n0z">De behandeling van een klacht verloopt in stappen.</p>
            <ol className="mt-6 grid gap-px overflow-hidden rounded-2xl bg-line ring-1 ring-line md:grid-cols-3" data-id="element-430" data-mp="3d2af4kbsy4qz">
              {steps.map((step, i) => <li key={step.title} className="bg-white p-6" data-id="element-431" data-mp="9kjv6n7qdwgf" data-mpi={i}>
                  <span className="font-display text-sm font-semibold text-primary" data-id="element-432" data-mp="9l1xo1kzl6ws">Stap {i + 1}</span>
                  <p className="mt-2 font-display text-lg font-semibold text-ink" data-id="element-433" data-mp="eitqqyxdaz2k">{step.title}</p>
                  <p className="mt-2 text-sm leading-relaxed text-muted" data-id="element-434" data-mp="3vzutgxgxxkxc">{step.text}</p>
                </li>)}
            </ol>
          </div>

          <div data-id="element-435" data-mp="siw90m9l7uic">
            <h2 className="font-display text-2xl font-semibold text-ink" data-id="element-436" data-mp="sicwxm6mc0c8">Wij gebruiken klachten om onze zorg te verbeteren</h2>
            <p className="mt-4 text-[17px] leading-relaxed text-muted" data-id="element-437" data-mp="xbfkndopsfki">
              Klachten, meldingen en signalen van onvrede helpen ons om te leren en te verbeteren. Excellent Care Clinics registreert
              deze meldingen, ook wanneer ze alleen ter kennisgeving of anoniem worden gedaan. Wij analyseren terugkerende
              onderwerpen en nemen waar nodig verbetermaatregelen. Onze klachtenprocedure is ingericht volgens de Wet kwaliteit,
              klachten en geschillen zorg (Wkkgz) en sluit aan bij de verwachtingen van de Inspectie Gezondheidszorg en Jeugd (IGJ).
            </p>
          </div>
        </div>

        <aside className="space-y-6 lg:sticky lg:top-32 lg:self-start" data-id="element-438" data-mp="eh638taif3ql">
          <div className="rounded-3xl bg-ink p-7 text-white" data-id="element-439" data-mp="efcvfao5udh0">
            <h2 className="font-display text-xl font-semibold" data-id="element-440" data-mp="9je35bzpobbb">Klachtenfunctionaris</h2>
            <ul className="mt-5 space-y-3 text-[15px]" data-id="element-441" data-mp="5h0xsoovmff2">
              <li data-id="element-442" data-mp="5ikay6f8kacm">
                <a href={`mailto:${finance.complaintsEmail}`} className="flex items-start gap-2 break-all text-cyan hover:text-white" data-id="element-443" data-mp="hwoly1ychoq">
                  <MailIcon className="mt-1 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-444" data-mp="2c48a3ul656o5" />
                  {finance.complaintsEmail}
                </a>
              </li>
              <li data-id="element-445" data-mp="2l5y1x1iyq7mg">
                <a href={clinic.phoneHref} className="flex items-center gap-2 text-cyan hover:text-white" data-id="element-446" data-mp="2l6r9u5tnr366">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-447" data-mp="2q3pyw9ygsjxi" />
                  {clinic.phone}
                </a>
              </li>
            </ul>
          </div>
          <dl className="divide-y divide-line rounded-3xl border border-line" data-id="element-448" data-mp="1xfludrrmqcwe">
            {facts.map(f => <div key={f.title} className="p-6" data-id="element-449" data-mp="22eye43n7f3ur">
                <dt className="font-display font-semibold text-ink" data-id="element-450" data-mp="22d3x30f1zvvy">{f.title}</dt>
                <dd className="mt-1.5 text-sm leading-relaxed text-muted" data-id="element-451" data-mp="27782hf8o7vux">{f.text}</dd>
              </div>)}
          </dl>
        </aside>
      </section>
    </>;
}